import CanvasJSReact from "@/assets/canvasjs.react";
import { rainbowColors } from "@/pages/Dashboard/ComparisonTools";
import { colorSchemaList } from "@/utils/colorSchemas";
import { dateFormatForCanvasJS } from "@/utils/globalUtils";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

const CanvasJS = CanvasJSReact.CanvasJS;
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const getMaxYValue = (dataPoints) => {
  return Math.max(...(dataPoints?.map((t) => t.y) || []));
};

const CanvasjsBarLineComboChart = ({
  title,
  subtitle,
  data = { line: [], bar: [] },
  yAxis,
  isDark = false,
  height,
  gridLine = true,
  verticalLine = true,
  legendPosition = "center bottom",
  markerType,
  gridDashType = "solid",
  markerColor,
  colorSchema,
  threshold = [],
  showThumbsUp = false,
  showAlarmedDatapoints = false,
  dateFormat = "D/M/YY hh:mm a",
}) => {
  const [key, setKey] = useState(Date.now());
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  let visible = { ...data?.line?.map((_, idx) => ({ idx: 0 })) };
  const scheme = colorSchemaList.others?.find((item) => item?.value === colorSchema);
  const colors = scheme?.colors || rainbowColors;

  CanvasJS.addColorSet("barShades", data?.line?.length === 1 && data?.bar?.length === 1 ? [colors[0]] : colors);

  const hideGridLine = {
    lineColor: !verticalLine && "transparent", // Hide Y-axis line
    lineThickness: verticalLine ? 1 : 0, // Hide Y-axis line
    gridThickness: 0, // Remove Y-axis lines in the background
  };

  const showGridLine = {
    gridColor: "lightgray",
    lineColor: "lightgray",
    tickColor: "lightgray",
    gridDashType,
    legendText: gridDashType,
    markerColor,
  };

  const fixImagePosition = () => {
    const chart = chartRef.current;
    if (chart && showThumbsUp && Array.isArray(data)) {
      Object.keys(visible).forEach((idx, actualIdx) => {
        let item = data[idx];
        const maxYValue = getMaxYValue(item?.data || item?.chart);
        const maxDataPoint = (item?.data || item?.chart || []).find((t) => t.y === maxYValue);
        const imageId = `thumbs-up-marker-${idx}`;

        const existingImg = chartContainerRef.current.querySelector(`#${imageId}`);

        if (maxDataPoint && existingImg) {
          const positionMarkerImage = () => {
            const pixelX = chart.axisX[0].convertValueToPixel(new Date(maxDataPoint.x));
            const pixelY = chart.axisY[0].convertValueToPixel(maxDataPoint.y);

            existingImg.style.left = `${pixelX - (Object.keys(visible).length / 2) * 20 + actualIdx * 20}px`;
            existingImg.style.top = `${pixelY - existingImg.clientHeight - 10}px`;
          };

          positionMarkerImage();

          window.addEventListener("resize", positionMarkerImage);

          return () => {
            window.removeEventListener("resize", positionMarkerImage);
            existingImg.remove();
          };
        }
      });
    }
  };

  const rerenderImage = () => {
    const chart = chartRef.current;
    if (chart && showThumbsUp && Array.isArray(data)) {
      data?.forEach((item, idx) => {
        const maxYValue = getMaxYValue(item?.data || item?.chart);
        const maxDataPoint = (item?.data || item?.chart || []).find((t) => t.y === maxYValue);

        const imageId = `thumbs-up-marker-${idx}`;

        const existingImg = chartContainerRef.current.querySelector(`#${imageId}`);
        if (existingImg) {
          existingImg.remove();
        }
        if (maxDataPoint) {
          const img = document.createElement("img");
          img.src = "/images/like.png";
          img.style.position = "absolute";
          img.style.height = "20px";
          img.style.width = "20px";
          img.style.zIndex = 10;
          // img.classList.add("thumbs-up-marker" + idx);
          img.id = imageId;

          chartContainerRef.current.querySelector(".canvasjs-chart-container").appendChild(img);

          const positionMarkerImage = () => {
            const pixelX = chart.axisX[0].convertValueToPixel(new Date(maxDataPoint.x));
            const pixelY = chart.axisY[0].convertValueToPixel(maxDataPoint.y);
            img.style.left = `${pixelX - (data?.length / 2) * 20 + idx * 20}px`;
            img.style.top = `${pixelY - img.clientHeight - 10}px`;
          };

          positionMarkerImage();

          window.addEventListener("resize", positionMarkerImage);

          return () => {
            window.removeEventListener("resize", positionMarkerImage);
            img.remove();
          };
        }
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      rerenderImage();
    }, 100);
    setKey(Date.now());
    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("resize", rerenderImage);
    };
  }, [data, chartRef, showThumbsUp]);

  useEffect(() => {
    fixImagePosition();
  }, [chartContainerRef?.current?.clientHeight, chartContainerRef?.current?.clientWidth]);

  return (
    <div
      className="nodrag nopan nowheel"
      ref={chartContainerRef}
      style={{ position: "relative", width: "100%", height: "100%", cursor: "auto" }}
      key={key}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <CanvasJSChart
        options={{
          dataPointWidth: 20,
          colorSet: "barShades",
          zoomEnabled: true,
          theme: isDark ? "dark2" : "light1",
          // animationEnabled: true,
          // animationDuration: 1000,
          height: height - 50 || chartContainerRef.current?.clientHeight - 50 || 350,
          title: {
            text: title,
            fontSize: 16,
            fontWeight: "bold",
          },
          subtitles: [
            {
              text: subtitle,
            },
          ],
          axisY: {
            includeZero: false,
            stripLines:
              Array.isArray(threshold) && threshold.length
                ? threshold?.map((data) => {
                    if (data) {
                      return { color: data?.color, value: data?.value };
                    }
                    return {};
                  })
                : [],
            labelFontSize: 12,
            // fontWeight: "bold",
            labelFontColor: "#b1adad",
            ...yAxis,
            ...(gridLine ? showGridLine : hideGridLine),
            lineThickness: verticalLine ? 1 : 0,
          },
          axisX: {
            labelWrap: true,
            valueFormatString: dateFormatForCanvasJS(dateFormat),
            labelFontSize: 12,
            labelAngle: 25,
            labelFontColor: "#b1adad",
            ...(gridLine ? showGridLine : hideGridLine),
          },
          legend: {
            cursor: "pointer",
            itemclick: function (e) {
              if (typeof e.dataSeries.visible === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;

                // Hide the corresponding thumbs-up image
                const imageId = `thumbs-up-marker-${e.dataSeriesIndex}`;
                const img = chartContainerRef.current.querySelector(`#${imageId}`);
                if (img) {
                  img.style.display = "none";
                }
                delete visible[e.dataSeriesIndex];
              } else {
                e.dataSeries.visible = true;
                visible[e.dataSeriesIndex] = 0;

                // Show the corresponding thumbs-up image
                const imageId = `thumbs-up-marker-${e.dataSeriesIndex}`;
                const img = chartContainerRef.current.querySelector(`#${imageId}`);
                if (img) {
                  img.style.display = "block";
                }
              }
              e.chart.render();
              fixImagePosition();
            },
            horizontalAlign: legendPosition.split(" ")[0] || "center", // left, center ,right
            verticalAlign: legendPosition.split(" ")[1] || "bottom", // top, center, bottom
            fontSize: 12,
            fontColor: "#b1adad",
            // fontWeight: "bold",
          },
          toolTip: {
            // shared: true,
            cornerRadius: 4,
          },
          data: [
            ...(data?.bar?.map((d) => ({
              type: "column",
              name: d.name || d._id,
              xValueFormatString: dateFormatForCanvasJS(dateFormat),
              showInLegend: legendPosition !== "hide",
              dataPointWidth: 20,
              dataPoints: (d.data || d.chart || []).map((t) => ({
                x: convertToMalaysiaTime(t.x),
                y: t.y,
                markerColor: markerColor
                  ? markerColor
                  : showAlarmedDatapoints
                    ? t.z && t.z !== "normal"
                      ? "red"
                      : "transparent"
                    : "",
                markerType: markerType || "circle",
                indexLabelPlacement: "outside",
                indexLabelFontSize: 32,
                indexLabelFontWeight: "bold",
              })),
            })) || []),

            ...(data?.line?.map((d) => {
              return {
                type: "spline",
                name: d.name || d._id,
                xValueFormatString: "DD MMM YYYY, hh:mm TT",
                showInLegend: legendPosition !== "hide",
                // lineDashType,
                lineTension: 1,
                dataPoints: (d.data || d.chart || []).map((t) => ({
                  // x: new Date(t.x),
                  x: convertToMalaysiaTime(t.x),
                  y: t.y,
                  markerColor: markerColor
                    ? markerColor
                    : showAlarmedDatapoints
                      ? t.z && t.z !== "normal"
                        ? "red"
                        : "transparent"
                      : "",
                  markerType: markerType || "circle",
                  indexLabelPlacement: "outside",
                  indexLabelFontSize: 32,
                  indexLabelFontWeight: "bold",
                })),
              };
            }) || []),
          ],
        }}
        onRef={(chart) => (chartRef.current = chart)}
      />
    </div>
  );
};

export default React.memo(CanvasjsBarLineComboChart);

CanvasjsBarLineComboChart.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  data: PropTypes.object,
  yAxis: PropTypes.string,
  isDark: PropTypes.bool,
  height: PropTypes.string,
  gridLine: PropTypes.bool,
  verticalLine: PropTypes.bool,
  legendPosition: PropTypes.string,
  markerType: PropTypes.string,
  gridDashType: PropTypes.string,
  markerColor: PropTypes.string,
  colorSchema: PropTypes.string,
  threshold: PropTypes.array,
  showThumbsUp: PropTypes.bool,
  showAlarmedDatapoints: PropTypes.bool,
  dateFormat: PropTypes.string,
};

const convertToMalaysiaTime = (dateStr) => {
  const date = new Date(dateStr); // Convert the string to a Date object
  const utcOffset = 8 * 60; // Malaysia is UTC+8
  const localTime = new Date(date.getTime() + utcOffset * 60000); // Adjust the time by adding the UTC offset in milliseconds
  return localTime;
};
